import { Globe, MapPin, Truck } from "lucide-react";
import type React from "react";
import { useLayoutEffect, useRef } from "react";
import {
  FULFILLMENT_LABELS,
  FULFILLMENT_MODE_ORDER,
  type FulfillmentMode,
} from "@/util/fulfillment";

const ICONS: Record<FulfillmentMode, React.FC<{ className?: string }>> = {
  PICKUP: MapPin,
  HOME_DELIVERY: Truck,
  NATIONWIDE: Globe,
};

const SLIDE_MS = 260;
const SLIDE_EASING = "cubic-bezier(0.32, 0.72, 0, 1)";

/**
 * Sliding segmented control: Pickup | Home Delivery | Nationwide. A single
 * mint indicator slides under the selected segment (Web Animations API, no
 * global keyframes). Behaves as a WAI-ARIA radio group with roving tabindex.
 */
export const FulfillmentModeSelector: React.FC<{
  value: FulfillmentMode;
  onChange: (mode: FulfillmentMode) => void;
  /** Segments are rendered only for modes the server currently offers. */
  availableModes: ReadonlyArray<FulfillmentMode>;
  pending?: boolean;
  className?: string;
}> = ({ value, onChange, availableModes, pending, className }) => {
  const options = FULFILLMENT_MODE_ORDER.filter((m) => availableModes.includes(m));
  const trackRef = useRef<HTMLDivElement>(null);
  const indicatorRef = useRef<HTMLSpanElement>(null);
  const lastRectRef = useRef<{ x: number; w: number } | null>(null);

  const measure = () => {
    const track = trackRef.current;
    const button = track?.querySelector<HTMLButtonElement>(`[data-mode="${value}"]`);
    if (!track || !button) return null;
    // The track is position: relative, so offsetLeft is already track-relative.
    return { x: button.offsetLeft, w: button.offsetWidth };
  };

  const place = (rect: { x: number; w: number }, animate: boolean) => {
    const el = indicatorRef.current;
    // A hidden tree (e.g. a suspended boundary) measures 0 — leave the last
    // real placement alone and wait for the reveal.
    if (!el || rect.w === 0) return;
    const from = lastRectRef.current;
    if (from && from.x === rect.x && from.w === rect.w) return;
    lastRectRef.current = rect;
    const reduceMotion =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!animate || !from || reduceMotion) {
      el.style.width = `${rect.w}px`;
      el.style.transform = `translateX(${rect.x}px)`;
      return;
    }
    const animation = el.animate(
      [
        { transform: `translateX(${from.x}px)`, width: `${from.w}px` },
        { transform: `translateX(${rect.x}px)`, width: `${rect.w}px` },
      ],
      { duration: SLIDE_MS, easing: SLIDE_EASING, fill: "forwards" },
    );
    animation.onfinish = () => {
      // Bake the end state into inline styles so fill:forwards animations
      // don't stack up across many switches.
      animation.commitStyles();
      animation.cancel();
    };
  };

  // DOM measurement, not derived state — a layout effect is the right tool.
  // Runs after every commit: `place` is a no-op unless the target moved, so
  // this also recovers when the tree is revealed after being hidden.
  // biome-ignore lint/correctness/useExhaustiveDependencies: measure/place read refs; runs every commit on purpose
  useLayoutEffect(() => {
    const rect = measure();
    if (rect) place(rect, true);
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: observer re-measures via refs; mount-only
  useLayoutEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const observer = new ResizeObserver(() => {
      const rect = measure();
      if (rect) place(rect, false);
    });
    observer.observe(track);
    for (const button of track.querySelectorAll("button")) observer.observe(button);
    return () => observer.disconnect();
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    const index = options.indexOf(value);
    if (index === -1) return;
    let next: number | null = null;
    switch (e.key) {
      case "ArrowRight":
      case "ArrowDown":
        next = (index + 1) % options.length;
        break;
      case "ArrowLeft":
      case "ArrowUp":
        next = (index - 1 + options.length) % options.length;
        break;
      case "Home":
        next = 0;
        break;
      case "End":
        next = options.length - 1;
        break;
      default:
        return;
    }
    e.preventDefault();
    const nextMode = options[next];
    if (!nextMode) return;
    onChange(nextMode);
    trackRef.current?.querySelector<HTMLButtonElement>(`[data-mode="${nextMode}"]`)?.focus();
  };

  return (
    <div className={className}>
      <div
        ref={trackRef}
        role="radiogroup"
        aria-label="How would you like to get your meals?"
        aria-busy={pending || undefined}
        className="relative inline-flex w-full sm:w-auto h-10 rounded-full bg-gray-100 p-1"
      >
        <span
          ref={indicatorRef}
          aria-hidden="true"
          className="absolute top-1 bottom-1 left-0 rounded-full bg-brand-mint pointer-events-none"
          style={{ width: 0, transform: "translateX(0px)" }}
        />
        {options.map((mode) => {
          const Icon = ICONS[mode];
          const selected = mode === value;
          return (
            // biome-ignore lint/a11y/useSemanticElements: segmented control — native radios can't host the sliding indicator; ARIA radio semantics + roving tabindex are implemented
            <button
              key={mode}
              type="button"
              role="radio"
              aria-checked={selected}
              tabIndex={selected ? 0 : -1}
              data-mode={mode}
              onClick={() => onChange(mode)}
              onKeyDown={handleKeyDown}
              className={`relative z-10 flex-1 sm:flex-none flex items-center justify-center gap-1 sm:gap-1.5 px-1.5 sm:px-3.5 rounded-full text-[13px] sm:text-sm font-medium whitespace-nowrap cursor-pointer transition-colors duration-200 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-mint ${
                selected ? "text-brand-black" : "text-gray-600 hover:text-brand-black"
              }`}
            >
              <Icon className="size-4 shrink-0" />
              {FULFILLMENT_LABELS[mode]}
            </button>
          );
        })}
      </div>
    </div>
  );
};
